import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Activity, Loader2, Cpu, ShieldAlert, Rocket, TrendingUp, RefreshCw } from "lucide-react";

const Stat = ({ label, value, hint, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-border bg-white p-4">
    <div className="text-[11px] uppercase tracking-wide text-obsidian/45">{label}</div>
    <div className="mt-1 text-2xl font-medium text-obsidian">{value ?? "—"}</div>
    {hint && <div className="mt-0.5 text-xs text-obsidian/45">{hint}</div>}
  </div>
);

const Section = ({ title, Icon, children }) => (
  <div className="mt-8">
    <div className="flex items-center gap-2 text-sm font-semibold text-obsidian/70">
      <Icon size={16} className="text-sage" /> {title}
    </div>
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">{children}</div>
  </div>
);

export default function CoachCanary() {
  const [m, setM] = useState(null);
  const [engine, setEngine] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/canary-metrics").then((r) => setM(r.data)).catch(() => {}),
      api.get("/admin/engine-state").then((r) => setEngine(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const a = m?.activation || {}, u = m?.usage || {}, ad = m?.adaptation || {},
    e = m?.engine || {}, b = m?.business || {}, r = m?.retention || {}, bs = m?.between_sessions || {};

  return (
    <CoachLayout>
      <div data-testid="admin-canary-page" className="max-w-4xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Activity size={22} className="text-sage" />
            <h1 className="text-2xl font-medium">Suivi Canary</h1>
          </div>
          <button data-testid="canary-refresh" onClick={load}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-obsidian/60 hover:bg-obsidian/[0.03]">
            <RefreshCw size={13} /> Rafraîchir
          </button>
        </div>
        <p className="mt-2 text-sm text-obsidian/55">
          Signal produit du canary : activation, entraînement, usage MAI, moteur, fin de cycle, business, rétention.
        </p>

        {engine && (
          <div data-testid="canary-engine-state" className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-[#E4F3EE] px-3 py-1 font-medium text-[#0A6E52]">
              Moteur : {engine.mai_engine_state}
            </span>
            <span className="rounded-full bg-obsidian/[0.04] px-3 py-1 text-obsidian/60">
              Coach IA : {engine.ai_cycle_coach_enabled ? "V2 actif" : "V1"}
            </span>
            <span className="rounded-full bg-obsidian/[0.04] px-3 py-1 text-obsidian/60">
              Validator : {engine.ai_plan_validator_mode}
            </span>
            <span className="rounded-full bg-obsidian/[0.04] px-3 py-1 text-obsidian/60">
              Trail : {engine.trail_enabled ? "on" : "off"}
            </span>
            <span className="rounded-full bg-obsidian/[0.04] px-3 py-1 text-obsidian/60">
              Entre-séances IA : {engine.between_sessions_ai_enabled ? "on" : "off"}
            </span>
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-sage" /></div>
        ) : !m ? (
          <div className="mt-10 text-sm text-obsidian/50">Aucune donnée disponible.</div>
        ) : (
          <>
            <Section title="Activation" Icon={Rocket}>
              <Stat testid="canary-clients" label="Clients" value={a.clients} />
              <Stat testid="canary-bilans-started" label="Bilans commencés" value={a.bilans_started} />
              <Stat testid="canary-bilans-done" label="Bilans terminés" value={a.bilans_done} />
              <Stat testid="canary-programs" label="Programmes générés" value={a.program_generated} />
            </Section>

            <Section title="Entraînement" Icon={TrendingUp}>
              <Stat label="Séances prévues" value={u.sessions_planned} />
              <Stat label="Séances réalisées" value={u.sessions_realized} />
              <Stat label="Taux de réalisation" value={u.realization_rate_pct != null ? `${u.realization_rate_pct}%` : "—"} />
              <Stat label="Feedbacks" value={u.feedbacks_recorded} />
            </Section>

            <Section title="Usage MAI & adaptation" Icon={Activity}>
              <Stat label="Appels IA" value={u.ai_calls} />
              <Stat label="Analyses hebdo" value={ad.week_analyses} />
              <Stat label="Adaptations" value={ad.adaptations} />
              <Stat label="Fin cycle 1" value={r.finished_first_cycle} hint="rétention" />
            </Section>

            <Section title="Entre deux séances (N→N+1)" Icon={Activity}>
              <Stat testid="canary-bs-keep" label="KEEP" value={bs.transitions ? `${bs.keep} (${bs.keep_rate_pct}%)` : "—"} />
              <Stat testid="canary-bs-adjust" label="ADJUST" value={bs.transitions ? `${bs.adjust} (${bs.adjust_rate_pct}%)` : "—"} />
              <Stat testid="canary-bs-replace" label="REPLACE" value={bs.transitions ? `${bs.replace} (${bs.replace_rate_pct}%)` : "—"} />
              <Stat testid="canary-bs-ai" label="Appels IA / transition" value={bs.transitions ? bs.ai_calls_per_transition : "—"} hint={`${bs.pain_safety_actions || 0} sécurité douleur`} />
            </Section>

            <Section title="Moteur" Icon={Cpu}>
              <Stat testid="canary-v2-gen" label="Générations V2" value={e.v2_generations} />
              <Stat testid="canary-v2-rate" label="Taux V2 nominal" value={e.v2_nominal_rate_pct != null ? `${e.v2_nominal_rate_pct}%` : "—"} hint={`${e.generation_attempts || 0} tentatives`} />
              <Stat testid="canary-fallbacks" label="Fallbacks techniques V1" value={e.v1_technical_fallbacks} hint={e.fallback_rate_pct != null ? `${e.fallback_rate_pct}%` : undefined} />
              <Stat testid="canary-refusals" label="Refus sécurité" value={e.safety_refusals} />
            </Section>

            {(e.errors > 0 || e.v1_direct > 0) && (
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="V1 direct" value={e.v1_direct} hint="flags V2 off" />
                <Stat label="Erreurs" value={e.errors} />
              </div>
            )}

            <Section title="Business" Icon={ShieldAlert}>
              <Stat label="Cycle offert utilisé" value={b.welcome_offer_used} />
              <Stat label="Paiements" value={b.paid_transactions} />
              <Stat label="Prépa complète" value={b.full_program_buyers} />
              <Stat label="Renouvellements" value={b.renewals} />
            </Section>
          </>
        )}
      </div>
    </CoachLayout>
  );
}
